import { Plugin } from '@nuxt/types';
import { Smartocto } from '@analytics/trackers';
import { Data as ArticleData } from '@root/modules/article/types/article';
import { convertToLocal } from '@root/common/filters/dateFormater';

type CustomParameters = Record<string, unknown>;

const smartoctoPlugin: Plugin = async (ctx) => {
  const { app, store } = ctx;
  const analytics = app.$channelConfig('analytics');

  if (!analytics?.smartocto?.apiToken) {
    return;
  }

  if (!analytics?.smartocto?.domainId) {
    return;
  }

  const smartocto = new Smartocto();
  await smartocto.init({ apiToken: analytics.smartocto?.apiToken, domainId: analytics.smartocto?.domainId });

  const smartoctoArticlePageView = (activeArticle: ArticleData) => {
    const { id } = app.$channelConfig('settings');
    const { hasChannelAccess } = app.$instanceConnector('paywall').state;

    let readerType = 'anonymous';

    if (app.$instanceConnector('customer').state.isLoggedIn) {
      readerType = 'registered';
    }

    if (activeArticle.content.paywall.enabled && hasChannelAccess) {
      readerType = 'subscribed';
    }

    let accessLevel = 'free';
    if (activeArticle.content.paywall.enabled) {
      accessLevel = 'paid';
    }

    const authors: Array<string> = [];
    if (activeArticle.authors.items.length) {
      activeArticle.authors.items.forEach((author) => {
        authors.push(author.name);
      });
    }

    const sections: Array<string> = [];
    sections.push(id[0].toUpperCase() + id.substring(1));

    const slug = activeArticle.primaryCategory.slug?.replace('-', ' ');
    if (slug) {
      sections.push(slug[0].toUpperCase() + slug.substring(1));
    }

    const tags: Array<string> = [];
    if (activeArticle.tags.items.length) {
      activeArticle.tags.items.forEach((tag) => {
        tags.push(tag.name);
      });
    }

    const articleParameters: CustomParameters = {
      postid: activeArticle.id,
      maincontent: '.C-article-info__title, .C-article-info__lead, .C-article__body',
      title: activeArticle.content.title.text,
      pubdate: convertToLocal(activeArticle.publishAt),
      authors: authors.length ? authors.join(', ') : 'Delfi',
      sections: sections.join('>'),
      tags: tags.join(','),
      article_type: 'news',
      access_level: accessLevel,
      reader_type: readerType,
    };

    smartocto.pageView(articleParameters);
  };

  // Watch activeArticle store state to send article page view
  store.watch(
    (state) => state.article.activeArticlePath,
    async (activeArticlePath) => {
      if (activeArticlePath && store.state.article.activeArticle) {
        const article = store.state.article.activeArticle;
        smartoctoArticlePageView(article);
      }
    },
    {
      immediate: true,
    }
  );

  // Watch customer token change to emit cXense page view event
  app.$instanceConnector('customer').listen('token', () => {
    if (store.state.article.activeArticle) {
      setTimeout(() => {
        const article = store.state.article.activeArticle;
        smartoctoArticlePageView(article);
      }, 1000);
    }
  });
};

export default smartoctoPlugin;
